/**
 * <s-wishlist-link> — Mobile bottom-bar Wishlist button for Wishlist by Square.
 *
 * - Tap: opens the app's saved list by clicking the app heart rendered into
 *   #vitals-wishlist-header (shows a short message if the app has not placed it yet).
 * - Any wishlist heart tap on the page: if the item wasn't saved yet, the product
 *   photo flies into the bottom-bar heart and the badge moves instantly.
 * - Badge: follows the header count; an instant guess is corrected by it within moments.
 */
class WishlistLink extends HTMLElement {
  #ac;
  #observer;
  #count = 0;
  #lastAppCount = null;
  #syncQueued = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#open();
    }, opts);

    // Capture phase so we read the heart's state before the app toggles it.
    document.addEventListener('click', (event) => this.#onWishlistTap(event), { ...opts, capture: true });

    this.#watchAppCounter();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#reconcileTimer);
  }

  get #badge() {
    return this.querySelector('[data-wishlist-count-badge]');
  }

  get #target() {
    return this.querySelector('[data-fly-wishlist-target]');
  }

  #open() {
    // Use exactly what the header wishlist heart uses: its link if it has one, otherwise its own click.
    const header = document.querySelector('header-actions') ?? document;
    const link = header.querySelector('a[href*="wishlist" i], [class*="Wishlist"] a[href], a[href][class*="Wishlist"], #vitals-wishlist-header a[href]');
    const href = link?.getAttribute('href');
    if (href && href !== '#' && !href.startsWith('javascript')) {
      window.location.assign(link.href);
      return;
    }

    const heart = header.querySelector('#vitals-wishlist-header [class*="Vtl-Wishlist"], [class*="Vtl-WishlistHeader"], #vitals-wishlist-header button, #vitals-wishlist-header [role="button"], [class*="Wishlist"] button, [class*="Wishlist"]:not(#vitals-wishlist-header)')
      ?? document.querySelector('[class*="Vtl-WishlistHeader"]');
    if (heart) {
      const target = heart.querySelector('button, [role="button"], svg') ?? heart;
      target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    } else if (this.dataset.unavailableMessage) {
      // The app hasn't placed its heart yet: tell the shopper instead of silently doing nothing.
      document.dispatchEvent(new CustomEvent('s-notification:add', {
        detail: { message: this.dataset.unavailableMessage, variant: 'warning' },
      }));
    }
  }

  #onWishlistTap(event) {
    if (!(event.target instanceof Element)) return;
    if (this.contains(event.target)) return;
    const heart = event.target.closest('[class*="Vtl-Wishlist"]');
    if (!heart || heart.closest('#vitals-wishlist-header, header-actions, [class*="Vtl-WishlistHeader"]')) return;

    // Instant response like add to cart: move the badge right away, then the app's
    // header count takes over the moment it updates (or restores it if nothing changed).
    const saved = this.#isSaved(heart);
    // Inside the hidden sync frame of a card heart: the card already handled the response.
    if (window.top !== window) return;
    this.#rememberProductPage(!saved);
    this.#showHeartInstantly(heart, !saved);
    this.#setCount(this.#count + (saved ? -1 : 1));
    this.#scheduleReconcile();
    if (!saved) this.#fly(this.#sourceImage(heart));
  }

  // Fill (or empty) the tapped heart right away instead of waiting for the app's
  // save round-trip; drop the override once the app shows the same state.
  #showHeartInstantly(heart, willBeSaved) {
    heart.dataset.wishlistOptimistic = willBeSaved ? 'saved' : 'removed';
    const done = () => {
      watcher.disconnect();
      clearTimeout(timer);
      delete heart.dataset.wishlistOptimistic;
    };
    const watcher = new MutationObserver(() => {
      if (this.#isSaved(heart) === willBeSaved) done();
    });
    watcher.observe(heart, { attributes: true, attributeFilter: ['class', 'aria-pressed', 'aria-checked'], subtree: true, childList: true });
    const timer = setTimeout(done, 5000);
  }

  #reconcileTimer;

  // Product page heart taps also update the saved state the card hearts read (lx-wishlist).
  #rememberProductPage(saved) {
    const handle = window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return;
    try {
      const set = new Set(JSON.parse(localStorage.getItem('lx-wishlist') || '[]'));
      if (saved) set.add(decodeURIComponent(handle));
      else set.delete(decodeURIComponent(handle));
      localStorage.setItem('lx-wishlist', JSON.stringify([...set]));
    } catch {
      /* storage unavailable */
    }
  }

  #scheduleReconcile() {
    clearTimeout(this.#reconcileTimer);
    this.#reconcileTimer = setTimeout(() => {
      if (this.#lastAppCount !== null) this.#setCount(this.#lastAppCount);
    }, 2500);
  }

  #isSaved(heart) {
    const nodes = [heart, ...heart.querySelectorAll('*')];
    return nodes.some((el) => {
      if (el.getAttribute('aria-pressed') === 'true' || el.getAttribute('aria-checked') === 'true') return true;
      const cls = typeof el.className === 'string' ? el.className : el.getAttribute('class') || '';
      return /(^|[\s_-])(active|added|selected|saved|filled|is-added|in-wishlist)($|[\s_-])/i.test(cls);
    });
  }

  #sourceImage(heart) {
    const card = heart.closest('li, article, [data-card], .product-card');
    const cardImg = card?.querySelector('img');
    if (cardImg) return cardImg;

    const inView = (img) => {
      const r = img.getBoundingClientRect();
      return r.width > 0 && r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight;
    };
    const gallery = [...document.querySelectorAll('[data-product-gallery] img')];
    return gallery.find(inView) ?? gallery[0] ?? null;
  }

  #fly(source) {
    const target = this.#target;
    if (!source || !target) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const to = target.getBoundingClientRect();
    if (!to.width) return;
    const from = source.getBoundingClientRect();
    const size = Math.min(from.width || 160, from.height || 160, 200);
    const startX = from.left + from.width / 2 - size / 2;
    const startY = from.top + from.height / 2 - size / 2;
    const dx = to.left + to.width / 2 - size / 2 - startX;
    const dy = to.top + to.height / 2 - size / 2 - startY;
    const scale = Math.max(to.width / size, 0.08);

    const flyer = document.createElement('img');
    flyer.src = source.currentSrc || source.src;
    flyer.alt = '';
    flyer.decoding = 'sync';
    flyer.setAttribute('aria-hidden', 'true');
    Object.assign(flyer.style, {
      position: 'fixed',
      left: `${startX}px`,
      top: `${startY}px`,
      width: `${size}px`,
      height: `${size}px`,
      objectFit: 'cover',
      borderRadius: '12px',
      boxShadow: '0 12px 30px rgb(0 0 0 / 0.25)',
      zIndex: '2147483000',
      pointerEvents: 'none',
      margin: '0',
    });
    document.body.append(flyer);

    const lift = Math.min(120, Math.abs(dy) * 0.3 + 40);
    flyer.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1, borderRadius: '12px' },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - lift}px) scale(0.55)`, opacity: 1, offset: 0.5 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.6, borderRadius: '50%' },
      ],
      { duration: 500, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
    ).finished.catch(() => {}).finally(() => {
      flyer.remove();
      target.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }],
        { duration: 250, easing: 'ease-out' }
      );
    });
  }

  #setCount(value) {
    this.#count = Math.max(0, value);
    const badge = this.#badge;
    if (!badge) return;
    badge.textContent = String(this.#count);
    badge.classList.toggle('hidden', this.#count === 0);
    badge.classList.toggle('flex', this.#count > 0);
  }

  // The app renders its header heart (with a counter) asynchronously; mirror that number.
  // Mirror exactly what the header wishlist heart shows. When the app hides its counter
  // (e.g. after removing everything) the bottom badge is hidden too.
  // What this page's header wishlist heart currently shows (0 when the app hides it).
  #readHeaderCount() {
    const isShown = (el) => {
      if (!el.isConnected || el.hidden) return false;
      const style = getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      return el.getClientRects().length > 0;
    };
    const header = document.querySelector('header-actions') ?? document.getElementById('vitals-wishlist-header');
    const counters = header
      ? [...header.querySelectorAll('[class*="HeartCounter"], [class*="Wishlist"] [class*="Counter"], [class*="Wishlist"] [class*="count" i]')]
      : [];
    const counter = counters.find(isShown);
    const parsed = counter ? Number.parseInt(counter.textContent.trim(), 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  #lastHeaderCount = null;

  // Follow the header heart whenever its own number changes. Card saves report the real
  // total separately (lx-wishlist:count), so a stale header can't pull the badge back.
  #watchAppCounter() {
    const sync = () => {
      this.#syncQueued = false;
      const value = this.#readHeaderCount();
      if (value === this.#lastHeaderCount) return;
      this.#lastHeaderCount = value;
      this.#lastAppCount = value;
      clearTimeout(this.#reconcileTimer);
      this.#setCount(value);
    };
    this.#observer = new MutationObserver(() => {
      if (this.#syncQueued) return;
      this.#syncQueued = true;
      requestAnimationFrame(sync);
    });
    this.#observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    sync();
  }
}

customElements.define('s-wishlist-link', WishlistLink);
